/**
 * Shared pure logic for group route guards.
 * No Pinia, no fetch – used by route middleware only.
 */

import { GroupType, MemberStatus } from '@communities/groups-layer/types/types'

import { VALID_GROUP_FIRST_SEGMENTS } from '../constants/group-route-segments'
import type { GroupData, UserMembershipData } from '../services/middleware-data.service'

export interface GroupRouteRedirect {
  redirect: 'home' | 'about' | 'not_found'
  accessDenied?: boolean
}

export function getGroupHomePath(groupSlug: string, isGokollab: boolean): string {
  return isGokollab ? `/${groupSlug}/home` : `/communities/groups/${groupSlug}/home`
}

export function getGroupAboutPath(groupSlug: string, isGokollab: boolean): string {
  return isGokollab ? `/${groupSlug}/about` : `/communities/groups/${groupSlug}/about`
}

export function getGroupNotFoundPath(isGokollab: boolean): string {
  return isGokollab ? '/' : '/communities/groups/not-found'
}

/** Default path prefix: /communities/groups/[groupSlug]/... */
const DEFAULT_GROUP_PATH_PREFIX = '/communities/groups/'

/**
 * Decode slug from URL path or route param so percent-encoded segments match router-decoded params.
 * Avoids redirect loops when `to.path` keeps `%C3%A8` but `to.params.groupSlug` is already UTF-8.
 */
export function normalizeGroupRouteSlug(slug: string): string {
  if (!slug || !slug.includes('%')) return slug
  try {
    return decodeURIComponent(slug)
  } catch {
    return slug
  }
}

/**
 * First segment after the dynamic [groupSlug] in path, or undefined if root.
 * Uses path structure (not indexOf) so a slug that matches a static segment
 * (e.g. groupSlug "groups" in /communities/groups/groups/home) is read correctly.
 */
export function getFirstSegmentAfterSlug(path: string, groupSlug: string): string | undefined {
  const normalized = path.replace(/\/+$/, '')
  const segments = normalized.split('/').filter(Boolean)
  const slugIndex = normalized.startsWith(DEFAULT_GROUP_PATH_PREFIX) ? 2 : 0
  const pathSlug = normalizeGroupRouteSlug(segments[slugIndex] ?? '')
  const paramSlug = normalizeGroupRouteSlug(groupSlug)
  if (pathSlug !== paramSlug) return undefined
  return segments[slugIndex + 1]
}

/**
 * Path is valid group route: has a first segment that is in VALID_GROUP_FIRST_SEGMENTS.
 * Root (no segment) or unknown segment → invalid.
 */
export function isValidGroupPath(path: string, groupSlug: string): boolean {
  const first = getFirstSegmentAfterSlug(path, groupSlug)
  if (first === undefined) return false
  return VALID_GROUP_FIRST_SEGMENTS.has(first)
}

export function hasAccessToGroup(
  group: GroupData['groupDetail'] | null | undefined,
  membership: UserMembershipData | null | undefined,
  isAuthenticated: boolean
): boolean {
  if (!group?.settings?.groupType) return true
  if (group.settings.groupType === GroupType.PUBLIC) return true
  if (group.settings.groupType !== GroupType.PRIVATE) return true
  if (!isAuthenticated) return false
  return membership?.status === MemberStatus.ACTIVE
}

export interface GetGroupRouteRedirectParams {
  path: string
  groupSlug: string
  group: GroupData | null
  membership: UserMembershipData | null
  isAuthenticated: boolean
}

/**
 * Returns redirect target or null if allow. accessDenied true when redirecting to about due to no access.
 */
export function getGroupRouteRedirect(params: GetGroupRouteRedirectParams): GroupRouteRedirect | null {
  const { path, groupSlug, group, membership, isAuthenticated } = params

  const groupDetail = group?.groupDetail ?? null
  if (!groupDetail) {
    return { redirect: 'not_found' }
  }

  const hasAccess = hasAccessToGroup(groupDetail, membership, isAuthenticated)
  const validPath = isValidGroupPath(path, groupSlug)
  const firstSegment = getFirstSegmentAfterSlug(path, groupSlug)

  // About page: always allow (avoid redirect loop)
  if (firstSegment === 'about') return null
  if (firstSegment === 'not-found') return null

  // Invalid route (root or unknown segment): redirect without accessDenied so no toast
  if (!validPath) {
    return hasAccess ? { redirect: 'home' } : { redirect: 'about' }
  }

  if (!hasAccess) {
    return { redirect: 'about', accessDenied: true }
  }

  return null
}
